import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
} from 'react';
import { Form, Steps, Button, Tabs, Skeleton, Typography, message } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from '@services/api';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { ICustomer, IUser } from '@pages/Access/types';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import ActionTypes from '@utils/deviceActions';
import CustomerInfoForm from './CustomerInfoForm';
import CustomerAdminForm from './CustomerAdminForm';
import CustomerPermissionsForm from './CustomerPermissionsForm';
import * as Styled from './styles';
import './styles.less';

const { Step } = Steps;
const { TabPane } = Tabs;
const { Paragraph } = Typography;

interface ICustomerViewerModalProps {
  customerId: number | undefined;
  visible: boolean;
  onClose: (close: boolean) => void;
  mutateData: (mutateType: 'CREATE' | 'DELETE', data: any) => void;
  isMotBusiness: boolean;
}

const CustomerModal: React.FC<ICustomerViewerModalProps> = ({
  customerId,
  visible,
  onClose,
  mutateData,
  isMotBusiness,
}) => {
  const { keycloak } = useContext(KeycloakContext);
  const { t } = useTranslation(['customers', 'users', 'attributes']);
  const [...forms] = [...Form.useForm(), ...Form.useForm(), ...Form.useForm()];
  const [isNewCustomer, setIsNewCustomer] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [customer, setCustomer] = useState<ICustomer>();
  const [roles, setRoles] = useState<string[]>();
  const [saveLoading, setSaveLoading] = useState(false);

  const { data: customerToUpdate } = useSwr<ICustomer>(
    customerId && customerId > 0 ? `/customers/${customerId}` : null,
  );
  const { data: newApnData } = useSwr(
    `/service-proxy/apns-customer/${keycloak?.idTokenParsed?.customerId}`,
  );

  const pageType = useMemo(
    () => (isMotBusiness ? 'department' : 'customer'),
    [isMotBusiness],
  );

  const createCustomer = useCallback(
    (payload: any) => api.post(`/customers/`, payload),
    [],
  );

  const updateCustomer = useCallback(
    (payload: any) => api.put(`/customers/${customerId}`, payload),
    [customerId],
  );

  useEffect(() => {
    setIsNewCustomer(customerId === -1);
  }, [customerId, visible]);

  const close = useCallback(
    (cancelled = false) => {
      forms.map(f => f.resetFields());
      setSaveLoading(false);
      setCurrentStep(0);
      setCustomer(undefined);
      setRoles(undefined);
      onClose(cancelled);
    },
    [forms, onClose],
  );

  const formattedRoles = useMemo(
    () => roles?.map((role: string) => ({ id: role })),
    [roles],
  );

  const formateDueDay = useCallback((dueDays: number[] | undefined) => {
    if (!dueDays || dueDays?.length === 0) return undefined;
    if (dueDays.length === 2) {
      const first = dueDays[0];
      const second = dueDays[1];
      return {
        dueDay: first > second ? second : first,
        secondDueDay: first > second ? first : second,
      };
    }
    return { dueDay: dueDays[0] };
  }, []);

  const handleActionUsages = useCallback((actionUsages: any) => {
    const usages = [];
    const suspensionUsages = actionUsages?.suspension;
    const cancellationUsages = actionUsages?.cancellation;
    if (suspensionUsages) {
      usages.push({
        actionId: ActionTypes.SUSPENSION,
        limit: suspensionUsages,
      });
    }

    if (cancellationUsages) {
      usages.push({
        actionId: ActionTypes.BROKER_CANCELLATION,
        limit: cancellationUsages,
      });
    }
    return usages;
  }, []);

  const onFinish = useCallback(
    async (values: any) => {
      setSaveLoading(true);
      if (isNewCustomer) {
        try {
          const { user, dueDay, ...newValues } = values;
          user.attributes.customerType = customer?.type;

          const apns = {
            apns: values.apns?.map((apn: string) => ({
              nameApn: apn,
              mainApn: true,
            })),
          };

          const payload = {
            ...newValues,
            ...apns,
            ...formateDueDay(dueDay),
            user: {
              ...user,
              password: undefined,
              credentials: [{ value: user.password }],
            },
            actionUsages: handleActionUsages(customer?.actionUsages),
          };

          const { data } = await createCustomer(payload);

          mutateData('CREATE', {
            ...data,
          });
          setCurrentStep(currentStep + 1);
        } catch (err) {
          const { response }: any = err;
          // username or email already exists
          if (response?.status === 409) {
            if (response?.data?.message?.includes('username')) {
              forms[2].setFields([
                {
                  name: 'username',
                  errors: [
                    t('users:modal.form.msgs.userNameAlreadyRegistered'),
                  ],
                },
              ]);
            }
            if (response?.data?.message?.includes('email')) {
              forms[2].setFields([
                {
                  name: 'email',
                  errors: [t('users:modal.form.msgs.emailAlreadyRegistered')],
                },
              ]);
            }
            setCurrentStep(2);

            // CPF/CNPJ already exists
          } else if (response?.status === 417) {
            forms[0].setFields([
              {
                name: 'cpfCnpj',
                errors: [t('form.msgs.cpfCnpjAlreadyRegistered')],
              },
            ]);
            setCurrentStep(0);
          } else {
            message.error(t(`msgs.error-creating-${pageType}`));
          }
        }
      } else {
        try {
          const addressUpdate = values?.address;
          const address = {
            id: customerToUpdate?.address?.id,
            ...addressUpdate,
          };

          const contactUpdate = values?.contact;
          const apns = {
            apns: values.apns?.map((apn: string) => ({
              nameApn: apn,
              mainApn: true,
            })),
          };
          const contact = {
            id: customerToUpdate?.contact?.id,
            ...contactUpdate,
          };

          const payload = {
            ...values,
            ...formateDueDay(values?.dueDay),
            ...apns,
            address,
            contact,
            actionUsages: handleActionUsages(values?.actionUsages),
          };

          await updateCustomer(payload);
          mutateData('CREATE', payload);
          close(false);
          message.success(t(`msgs.success-updating-${pageType}`));
        } catch {
          message.error(t(`msgs.error-updating-${pageType}`));
        }
      }
      setSaveLoading(false);
    },
    [
      isNewCustomer,
      customer?.type,
      customer?.actionUsages,
      formateDueDay,
      handleActionUsages,
      createCustomer,
      mutateData,
      currentStep,
      forms,
      t,
      pageType,
      customerToUpdate?.address?.id,
      customerToUpdate?.contact?.id,
      updateCustomer,
      close,
    ],
  );

  const formValidate = useCallback(() => {
    // in the 2nd step the dataUser comes the user data
    forms[currentStep].validateFields().then((dataUser: IUser) => {
      forms[currentStep].submit();
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        onFinish({ ...customer, roles: formattedRoles, user: dataUser });
      }
    });
  }, [currentStep, customer, formattedRoles, forms, onFinish]);

  // clear permissions error
  useEffect(() => {
    if (formattedRoles && formattedRoles?.length > 0) {
      forms[1].setFields([
        {
          name: 'roles',
          errors: [],
        },
      ]);
    }
  }, [formattedRoles, forms]);

  const nextStep = useCallback(() => {
    if (currentStep === 1) {
      if (!formattedRoles || formattedRoles?.length === 0) {
        forms[1].setFields([
          {
            name: 'roles',
            errors: [`* ${t('roles:modal.form.msgs.chooseAtLeastOne')}`],
          },
        ]);
      } else {
        setCurrentStep(oldCurrentStep => oldCurrentStep + 1);
      }
    } else {
      formValidate();
    }
  }, [currentStep, formValidate, formattedRoles, forms, t]);

  const handleUpdateFinish = useCallback(() => {
    forms[0].validateFields().then((customerData: ICustomer) => {
      onFinish(customerData);
    });
  }, [forms, onFinish]);

  return (
    <Styled.ModalContainer
      afterClose={() => document.body.style.removeProperty('overflow')}
      footer={
        currentStep < forms?.length && (
          <>
            {currentStep > 0 && !saveLoading && currentStep < forms?.length && (
              <Button onClick={() => setCurrentStep(currentStep - 1)}>
                {t('buttons.backButton')}
              </Button>
            )}

            {isNewCustomer ? (
              <Button type="primary" onClick={nextStep} loading={saveLoading}>
                {t(
                  `buttons.${
                    currentStep < forms.length - 1
                      ? 'nextButton'
                      : 'createButton'
                  }`,
                )}
              </Button>
            ) : (
              <Button
                loading={saveLoading}
                type="primary"
                onClick={handleUpdateFinish}
              >
                {t('buttons.saveButton')}
              </Button>
            )}
          </>
        )
      }
      maskClosable={false}
      okButtonProps={{ form: 'customerForm', htmlType: 'submit' }}
      title={t(
        `modal.title.${isNewCustomer ? 'new' : 'update'}${
          isMotBusiness ? 'Department' : 'Customer'
        }`,
      )}
      visible={visible}
      width="100%"
      centered
      forceRender
      onCancel={() => {
        document.body.style.removeProperty('overflow');
        close(true);
      }}
    >
      {isNewCustomer ? (
        <>
          {currentStep < forms?.length ? (
            <>
              <Steps
                current={currentStep}
                size="small"
                className="antd-steps-custom"
              >
                <Step title={t('modal.stepsTitles.information')} />
                <Step title={t('modal.stepsTitles.permissions')} />
                <Step title={t('modal.stepsTitles.admin')} />
              </Steps>

              <Tabs activeKey={`${currentStep}`} className="modal-tab">
                <TabPane key="0" style={{ padding: 24 }}>
                  <CustomerInfoForm
                    form={forms[0]}
                    isNewCustomer={isNewCustomer}
                    onFormFinish={setCustomer}
                    newApnData={newApnData}
                  />
                </TabPane>
                <TabPane key="1" style={{ padding: 24 }}>
                  <CustomerPermissionsForm
                    form={forms[1]}
                    onFormFinish={setRoles}
                    parentIdSelected={forms[0].getFieldValue('parentId')}
                    customerTypeWillCreated={forms[0].getFieldValue('type')}
                  />
                </TabPane>
                <TabPane key="2" style={{ padding: 24 }}>
                  <CustomerAdminForm form={forms[2]} />
                </TabPane>
              </Tabs>
            </>
          ) : (
            <Skeleton loading={!forms[2]} active>
              <div className="final-step">
                <CheckCircleOutlined className="final-step-circle" />
                <Paragraph className="final-step-paragraph">
                  {t('modal.ready', {
                    type: t(`attributes:${pageType}`),
                  })}
                  <p>
                    {`${t('attributes:username')}:`}
                    <strong>{forms[2].getFieldValue('username')}</strong>
                    {`${t('attributes:email')}:`}
                    <strong>{forms[2].getFieldValue('email')}</strong>
                  </p>
                </Paragraph>
              </div>
            </Skeleton>
          )}
        </>
      ) : (
        <div style={{ padding: '0 24px' }}>
          <CustomerInfoForm
            customerToUpdate={customerToUpdate}
            form={forms[0]}
            isNewCustomer={isNewCustomer}
            onFormFinish={setCustomer}
            isMotBusiness={isMotBusiness}
            newApnData={newApnData}
          />
        </div>
      )}
    </Styled.ModalContainer>
  );
};

export default CustomerModal;
